<template>
  <div class="chart-wrapper" :style="{ height: `${height}px` }">
    <canvas ref="chartRef"></canvas>
  </div>
</template>

<script setup lang="ts">
import Chart from "@/chart-setup";
import { LineDataset } from "@/types/ChartData";
import { type ChartConfiguration, type TooltipItem } from "chart.js";
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
  watch,
} from "vue";

const props = withDefaults(
  defineProps<{
    chartData: {
      labels: string[];
      datasets: LineDataset[];
    };
    title?: string;
    subtitle?: string;
    yAxisTitle?: string;
    height?: number;
    showLegend?: boolean;
    legendPosition?: "top" | "bottom" | "left" | "right";
    /**
     * Which points a hover picks up. "index" reads the whole column (the default, good for a
     * handful of comparable series); "nearest" isolates the single hovered line, which is the
     * only readable option once there are many series.
     */
    interactionMode?: "index" | "nearest";
    /** Replaces the default "<series>: <value> <unit>" tooltip line. Return an array for several lines. */
    tooltipLabel?: (context: TooltipItem<"line">) => string | string[];
    yScaleType?: "linear" | "logarithmic";
    /**
     * Pins the y-axis floor. Required on a logarithmic axis whose data contains substituted
     * zeros: left to itself, Chart.js drops the floor a decade below the *largest* value, which
     * pushes small series off the bottom of the chart entirely.
     */
    yMin?: number;
  }>(),
  {
    title: "",
    subtitle: "",
    yAxisTitle: "GWh",
    height: 300,
    showLegend: true,
    legendPosition: "bottom",
    interactionMode: "index",
    tooltipLabel: undefined,
    yScaleType: "linear",
    yMin: undefined,
  }
);

const chartRef = useTemplateRef<HTMLCanvasElement>("chartRef");
let chart: Chart<"line"> | null = null;

const createChart = () => {
  if (chartRef.value) {
    const ctx = chartRef.value.getContext("2d");
    if (ctx) {
      const config: ChartConfiguration<"line"> = {
        type: "line",
        data: props.chartData,
        options: {
          interaction: {
            mode: props.interactionMode,
            intersect: false,
          },
          plugins: {
            legend: {
              display: props.showLegend,
              position: props.legendPosition,
            },
            title: {
              display: !!props.title,
              text: props.title,
              font: {
                size: 16,
                family: "Nunito Sans",
              },
              padding: {
                top: 5,
                bottom: 0,
              },
            },
            subtitle: {
              display: !!props.subtitle,
              text: props.subtitle,
              font: {
                size: 14,
                style: "italic",
                family: "Nunito Sans",
              },
              padding: {
                top: 0,
                bottom: 0,
              },
            },
            tooltip: {
              callbacks: {
                label:
                  props.tooltipLabel ??
                  function (context: TooltipItem<"line">) {
                    let label = context.dataset.label || "";
                    if (label) {
                      label += ": ";
                    }
                    if (context.parsed.y !== null) {
                      label +=
                        Math.round(context.parsed.y).toLocaleString() +
                        " " +
                        props.yAxisTitle;
                    }
                    return label;
                  },
              },
            },
          },
          scales: {
            x: {
              display: true,
            },
            y: {
              type: props.yScaleType,
              beginAtZero: props.yScaleType === "linear",
              min: props.yMin,
              title: { display: true, text: props.yAxisTitle },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
        },
      };

      chart = new Chart(ctx, config);
    }
  }
};

const updateTitle = (chart: Chart<"line">) => {
  if (chart?.options?.plugins?.title) {
    chart.options.plugins.title.text = props.title;
    chart.options.plugins.title.display = !!props.title;
  }
};

const updateAxisTitle = (chart: Chart<"line">) => {
  // scales.y is typed as a union over every scale kind and only the cartesian members carry
  // `title`; this component only ever builds a cartesian y axis.
  const yScale = chart?.options?.scales?.y as
    { title?: { text?: string } } | undefined;
  if (yScale?.title) {
    yScale.title.text = props.yAxisTitle;
  }
};

const updateChart = () => {
  if (chart) {
    chart.data = props.chartData;
    updateTitle(chart);
    updateAxisTitle(chart);
    if (chart.options?.plugins?.legend) {
      chart.options.plugins.legend.display = props.showLegend;
      chart.options.plugins.legend.position = props.legendPosition;
    }
    chart.update();
  }
};

onMounted(async () => {
  await nextTick();
  createChart();
});

onBeforeUnmount(() => {
  if (chart) {
    chart.destroy();
    chart = null;
  }
});

watch(() => props.chartData, updateChart, { deep: true });
watch(() => props.title, updateChart);
watch(() => props.yAxisTitle, updateChart);
watch(() => props.showLegend, updateChart);
watch(() => props.legendPosition, updateChart);
watch(
  () => props.height,
  async () => {
    await nextTick();
    if (chart) {
      chart.resize();
    }
  }
);
watch([() => props.yScaleType, () => props.yMin], async () => {
  chart?.destroy();
  chart = null;
  await nextTick();
  createChart();
});
</script>

<style scoped lang="scss">
.chart-wrapper {
  position: relative;
  margin-bottom: 15px;
}
</style>
